<template>
  <div class="tw:flex tw:flex-col tw:gap-3" :style="{ maxWidth: `${maxWidth}px` }">
    <!-- a group so a screen reader announces one labelled control, not six blank fields -->
    <div role="group" :aria-label="ariaLabel" class="tw:grid tw:grid-cols-6" :class="gapClass">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        :ref="(el) => setBoxRef(el as HTMLInputElement | null, index)"
        :value="digit"
        :aria-label="boxLabel(index)"
        :aria-invalid="hasError || undefined"
        :disabled="disabled"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="1"
        class="tw:w-full tw:rounded-md tw:border tw:bg-surface tw:text-center tw:font-semibold tw:text-gray-900 tw:shadow-[0_1px_2px_0_rgb(16_24_40_/_0.05)] tw:outline-none tw:transition-colors tw:disabled:cursor-not-allowed tw:disabled:bg-gray-50"
        :class="[boxSizeClass, hasError ? errorBoxClass : normalBoxClass]"
        @beforeinput="onBeforeInput"
        @input="onInput($event, index)"
        @keydown="onKeydown($event, index)"
        @paste="onPaste"
        @focus="onFocus(index)" />
    </div>

    <div v-if="showPasteHint || $slots.aside" class="tw:flex tw:items-center tw:justify-between tw:gap-4">
      <span v-if="showPasteHint" class="tw:text-13 tw:text-muted">{{ $t('registration.code.paste_works') }}</span>
      <slot name="aside" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

interface Props {
  /** number of boxes */
  length?: number;
  /** 'lg' is the standalone email screen, 'md' the inline card on the profile step */
  size?: 'lg' | 'md';
  disabled?: boolean;
  hasError?: boolean;
  ariaLabel?: string;
  /** the mobile card has its own hint line instead */
  showPasteHint?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  length: 6,
  size: 'lg',
  ariaLabel: undefined,
  showPasteHint: true,
});

const emit = defineEmits<{
  /** fired once every box is filled */
  complete: [code: string];
}>();

const model = defineModel<string>({ default: '' });

/* ---------------------------------- STATE ---------------------------------- */
const boxes = ref<(HTMLInputElement | null)[]>([]);

/* --------------------------------- COMPUTED -------------------------------- */
const digits = computed(() => Array.from({ length: props.length }, (_, index) => model.value[index] ?? ''));

const isLarge = computed(() => props.size === 'lg');

const maxWidth = computed(() => (isLarge.value ? 400 : 328));

const gapClass = computed(() => (isLarge.value ? 'tw:gap-2.5' : 'tw:gap-2'));

const boxSizeClass = computed(() => (isLarge.value ? 'tw:h-14 tw:text-[24px]' : 'tw:h-11 tw:text-18'));

const normalBoxClass = 'tw:border-gray-300 tw:focus:border-primary tw:focus:shadow-[0_0_0_4px_rgb(0_122_255_/_0.10)]';

const errorBoxClass = 'tw:border-danger tw:focus:shadow-[0_0_0_4px_rgb(214_57_57_/_0.10)]';

/* --------------------------------- METHODS --------------------------------- */
function setBoxRef(el: HTMLInputElement | null, index: number) {
  boxes.value[index] = el;
}

function boxLabel(index: number) {
  return `${index + 1}`;
}

function focusBox(index: number) {
  const clamped = Math.min(Math.max(index, 0), props.length - 1);
  const box = boxes.value[clamped];
  if (!box) return;

  box.focus();
  box.select();
}

function write(value: string) {
  // judge the written value, not model.value, which lags one render behind and would never emit `complete`
  const normalized = value.slice(0, props.length);
  model.value = normalized;
  if (normalized.length === props.length) emit('complete', normalized);
}

function replaceAt(index: number, digit: string) {
  const next = digits.value.slice();
  next[index] = digit;

  // trim trailing blanks so the model length is the real digit count
  write(next.join('').replace(/\s+$/, ''));
}

/** digits only, refused before the character shows so a letter never flashes in */
function onBeforeInput(event: Event) {
  const data = (event as InputEvent).data;
  if (data === null || data === undefined) return;

  // multi-character insertions (paste, autofill) are handled by the paste/distribute path
  if (data.length === 1 && !/\d/.test(data)) event.preventDefault();
}

function onInput(event: Event, index: number) {
  const input = event.target as HTMLInputElement;
  const typed = input.value.replace(/\D/g, '');

  // autofill or an engine-routed paste arrives as several digits in one box
  if (typed.length > 1) {
    distribute(typed, index);
    return;
  }

  replaceAt(index, typed);

  // resync the DOM when the model didn't change, e.g. retyping the same digit
  input.value = typed;

  if (typed) focusBox(index + 1);
}

function onKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'Backspace') {
    // on an empty box, backspace steps back and clears
    if (!digits.value[index]) {
      event.preventDefault();
      replaceAt(index - 1 < 0 ? 0 : index - 1, '');
      focusBox(index - 1);
    }
    return;
  }

  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    focusBox(index - 1);
    return;
  }

  if (event.key === 'ArrowRight') {
    event.preventDefault();
    focusBox(index + 1);
  }
}

function onPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text') ?? '';
  event.preventDefault();

  // always from the first box: a pasted code is the whole code
  distribute(pasted, 0);
}

function distribute(raw: string, fromIndex: number) {
  const incoming = raw.replace(/\D/g, '');
  if (!incoming) return;

  const next = digits.value.slice();
  for (let offset = 0; offset < incoming.length && fromIndex + offset < props.length; offset += 1) {
    next[fromIndex + offset] = incoming[offset];
  }

  write(next.join('').replace(/\s+$/, ''));
  nextTick(() => focusBox(Math.min(fromIndex + incoming.length, props.length - 1)));
}

/** clicking past a gap in a half-typed code lands on the first gap */
function onFocus(index: number) {
  // read live box values, not the model, which lags one render behind
  const firstEmpty = boxes.value.findIndex((box) => box === null || box.value === '');
  if (firstEmpty !== -1 && firstEmpty < index) focusBox(firstEmpty);
}

/* --------------------------------- WATCHERS -------------------------------- */
// a cleared model (e.g. a rejected code) returns focus to the first box
watch(model, (value, previous) => {
  if (value === '' && previous !== '') nextTick(() => focusBox(0));
});

defineExpose({ focusFirst: () => focusBox(0) });
</script>
